import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { fetchAppUserById, updateAppUser, fetchProfessions, fetchStates, fetchDistrictsByState, fetchTalukasByDistrict } from "../utils/api";

const EditAppUser = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
    mobile: "",
    email: "",
    dob: "",
    address: "",
    town: "",
    pinCode: "",
    profession: {
      professionId: ""
    },
    state: {
      stateId: ""
    },
    district: {
      districtId: ""
    },
    taluka: {
      talukaId: ""
    }
  });
  const [originalUser, setOriginalUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [professions, setProfessions] = useState([]);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [talukas, setTalukas] = useState([]);
  const [loadingProfessions, setLoadingProfessions] = useState(false);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [loadingTalukas, setLoadingTalukas] = useState(false);
  const navigateTimeoutRef = useRef(null);

  useEffect(
    () => () => {
      if (navigateTimeoutRef.current != null) {
        clearTimeout(navigateTimeoutRef.current);
        navigateTimeoutRef.current = null;
      }
    },
    []
  );

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch user data
        const userResponse = await fetchAppUserById(id);
        if (cancelled) return;
        const userData = userResponse.data;

        if (!userData) {
          throw new Error('User not found');
        }

        // Fetch dropdown options
        setLoadingProfessions(true);
        setLoadingStates(true);

        const [professionsResponse, statesResponse] = await Promise.all([
          fetchProfessions().catch(() => ({ data: [] })),
          fetchStates().catch(() => ({ data: [] }))
        ]);
        if (cancelled) return;

        setProfessions(professionsResponse.data || []);
        setStates(statesResponse.data || []);
        setLoadingProfessions(false);
        setLoadingStates(false);

        // Prepare user data for form
        const formData = {
          firstName: userData.firstName || "",
          middleName: userData.middleName || "",
          lastName: userData.lastName || "",
          mobile: userData.mobile || "",
          email: userData.email || "",
          dob: userData.dob || "",
          address: userData.address || "",
          town: userData.town || "",
          pinCode: userData.pinCode || "",
          profession: {
            professionId: userData.profession?.professionId || ""
          },
          state: {
            stateId: userData.state?.stateId || ""
          },
          district: {
            districtId: userData.district?.districtId || ""
          },
          taluka: {
            talukaId: userData.taluka?.talukaId || ""
          }
        };

        setUser(formData);
        setOriginalUser(userData);

        // Load districts if state is selected
        if (formData.state.stateId) {
          setLoadingDistricts(true);
          try {
            const districtsResponse = await fetchDistrictsByState(formData.state.stateId);
            if (cancelled) return;
            setDistricts(districtsResponse.data || []);
          } catch (error) {
            console.error("Error fetching districts:", error);
          } finally {
            if (!cancelled) setLoadingDistricts(false);
          }
        }

        // Load talukas if district is selected
        if (formData.district.districtId) {
          setLoadingTalukas(true);
          try {
            const talukasResponse = await fetchTalukasByDistrict(formData.district.districtId);
            if (cancelled) return;
            setTalukas(talukasResponse.data || []);
          } catch (error) {
            console.error("Error fetching talukas:", error);
          } finally {
            if (!cancelled) setLoadingTalukas(false);
          }
        }
      } catch (error) {
        console.error("Error fetching user:", error);
        if (!cancelled) setError("Failed to load user data. Please try again.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    
    if (name === "professionId") {
      setUser({
        ...user,
        profession: { professionId: value }
      });
    } else if (name === "stateId") {
      setUser({
        ...user,
        state: { stateId: value },
        district: { districtId: "" },
        taluka: { talukaId: "" }
      });
      setDistricts([]);
      setTalukas([]);
      
      if (value) {
        setLoadingDistricts(true);
        fetchDistrictsByState(value)
          .then(response => {
            setDistricts(response.data || []);
          })
          .catch(error => {
            console.error("Error fetching districts:", error);
            setDistricts([]);
          })
          .finally(() => {
            setLoadingDistricts(false);
          });
      }
    } else if (name === "districtId") {
      setUser({
        ...user,
        district: { districtId: value },
        taluka: { talukaId: "" }
      });
      setTalukas([]);
      
      if (value) {
        setLoadingTalukas(true);
        fetchTalukasByDistrict(value)
          .then(response => {
            setTalukas(response.data || []);
          })
          .catch(error => {
            console.error("Error fetching talukas:", error);
            setTalukas([]);
          })
          .finally(() => {
            setLoadingTalukas(false);
          });
      }
    } else if (name === "talukaId") {
      setUser({
        ...user,
        taluka: { talukaId: value }
      });
    } else {
      setUser({ ...user, [name]: value });
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    
    try {
      const userName = `${user.firstName || ''} ${user.middleName || ''} ${user.lastName || ''}`.trim() || user.mobile || `User ${id}`;
      
      await updateAppUser(id, user, {
        previousData: originalUser,
        identifier: userName
      });
      
      setSuccessMessage("User updated successfully!");
      if (navigateTimeoutRef.current != null) clearTimeout(navigateTimeoutRef.current);
      navigateTimeoutRef.current = setTimeout(() => {
        navigateTimeoutRef.current = null;
        navigate("/dashboard/users");
      }, 2000);
    } catch (error) {
      console.error("Error updating user:", error);
      setError("Failed to update user. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate("/dashboard/users");
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 900, mx: "auto", mt: 4, p: 2 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Edit Registered User
        </Typography>
        
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}
        
        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="First Name"
                name="firstName"
                value={user.firstName || ""}
                onChange={handleInputChange}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Middle Name"
                name="middleName"
                value={user.middleName || ""}
                onChange={handleInputChange}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Last Name"
                name="lastName"
                value={user.lastName || ""}
                onChange={handleInputChange}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Mobile"
                name="mobile"
                value={user.mobile || ""}
                onChange={handleInputChange}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={user.email || ""}
                onChange={handleInputChange}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Date of Birth"
                name="dob"
                type="date"
                value={user.dob || ""}
                onChange={handleInputChange}
                margin="normal"
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="normal" disabled={loadingProfessions}>
                <InputLabel id="profession-label">Profession</InputLabel>
                <Select
                  labelId="profession-label"
                  name="professionId"
                  value={user.profession?.professionId || ""}
                  label="Profession"
                  onChange={handleInputChange}
                >
                  {professions.map((prof) => (
                    <MenuItem key={prof.professionId} value={prof.professionId}>
                      {prof.professionName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                name="address"
                value={user.address || ""}
                onChange={handleInputChange}
                margin="normal"
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Town"
                name="town"
                value={user.town || ""}
                onChange={handleInputChange}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Pin Code"
                name="pinCode"
                value={user.pinCode || ""}
                onChange={handleInputChange}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth margin="normal" disabled={loadingStates}>
                <InputLabel id="state-label">State</InputLabel>
                <Select
                  labelId="state-label"
                  name="stateId"
                  value={user.state?.stateId || ""}
                  label="State"
                  onChange={handleInputChange}
                >
                  {states.map((state) => (
                    <MenuItem key={state.stateId} value={state.stateId}>
                      {state.stateName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth margin="normal" disabled={!user.state?.stateId || loadingDistricts}>
                <InputLabel id="district-label">District</InputLabel>
                <Select
                  labelId="district-label"
                  name="districtId"
                  value={user.district?.districtId || ""}
                  label="District"
                  onChange={handleInputChange}
                >
                  {districts.map((district) => (
                    <MenuItem key={district.districtId} value={district.districtId}>
                      {district.districtName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth margin="normal" disabled={!user.district?.districtId || loadingTalukas}>
                <InputLabel id="taluka-label">Taluka</InputLabel>
                <Select
                  labelId="taluka-label"
                  name="talukaId"
                  value={user.taluka?.talukaId || ""}
                  label="Taluka"
                  onChange={handleInputChange}
                >
                  {talukas.map((taluka) => (
                    <MenuItem key={taluka.talukaId} value={taluka.talukaId}>
                      {taluka.talukaName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          
          <Box sx={{ mt: 3, display: "flex", justifyContent: "space-between" }}>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleCancel}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={saving}
            >
              {saving ? <CircularProgress size={24} /> : "Save Changes"}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default EditAppUser;
